(() => {
  const selector = '[data-xsto-product-360]';

  function modulo(value, length) {
    return ((value % length) + length) % length;
  }

  function initialize(viewer) {
    if (viewer.dataset.initialized === 'true') return;
    viewer.dataset.initialized = 'true';

    const stage = viewer.querySelector('[data-xsto-360-stage]');
    const image = viewer.querySelector('[data-xsto-360-image]');
    const status = viewer.querySelector('[data-xsto-360-status]');
    const hint = viewer.querySelector('[data-xsto-360-hint]');
    const retryButton = viewer.querySelector('[data-xsto-360-retry]');
    const controls = [...viewer.querySelectorAll('[data-step]')];
    const manifestUrl = viewer.dataset.manifestUrl;

    if (!stage || !image || !status || !manifestUrl) return;

    let frames = [];
    let manifestPromise;
    let currentIndex = 0;
    let targetIndex = 0;
    let direction = 1;
    let drag = null;
    let loadGeneration = 0;
    const loadedFrames = new Map([[0, image.currentSrc || image.src]]);
    const loadingFrames = new Map();

    function setStatus(message) {
      status.textContent = message;
      status.hidden = !message;
    }

    function formatIndex(index) {
      return `${index + 1} / ${frames.length}`;
    }

    function loadFrame(index) {
      const normalizedIndex = modulo(index, frames.length);
      if (loadedFrames.has(normalizedIndex)) {
        return Promise.resolve(loadedFrames.get(normalizedIndex));
      }
      if (loadingFrames.has(normalizedIndex)) return loadingFrames.get(normalizedIndex);

      const frameImage = new Image();
      frameImage.decoding = 'async';
      const loading = new Promise((resolve, reject) => {
        frameImage.addEventListener('load', async () => {
          try {
            if (typeof frameImage.decode === 'function') await frameImage.decode();
          } catch {
            // Keep the loaded file usable if the browser does not expose decode().
          }
          loadedFrames.set(normalizedIndex, frames[normalizedIndex].url);
          loadingFrames.delete(normalizedIndex);
          resolve(frames[normalizedIndex].url);
        }, { once: true });
        frameImage.addEventListener('error', () => {
          loadingFrames.delete(normalizedIndex);
          reject(new Error('frame-load-failed'));
        }, { once: true });
      });

      loadingFrames.set(normalizedIndex, loading);
      frameImage.src = frames[normalizedIndex].url;
      return loading;
    }

    function warmNearbyFrames(centerIndex, travelDirection) {
      // Keep just a short runway ready. The remaining images stay on the CDN
      // until the visitor actually turns the product toward them.
      for (const offset of [1, 2]) {
        const neighborIndex = modulo(centerIndex + travelDirection * offset, frames.length);
        loadFrame(neighborIndex).catch(() => {});
      }
    }

    async function showFrame(nextIndex, travelDirection = direction) {
      if (!frames.length) return;
      targetIndex = modulo(nextIndex, frames.length);
      direction = travelDirection || direction;
      const generation = ++loadGeneration;
      viewer.dataset.loading = 'true';
      if (retryButton) retryButton.hidden = true;
      setStatus(`角度を切り替えています… ${formatIndex(targetIndex)}`);

      try {
        const nextUrl = await loadFrame(targetIndex);
        if (generation !== loadGeneration) return;

        // The current frame remains visible until the requested one is ready.
        // Replacing src only after preload avoids a blank flash while dragging.
        image.src = nextUrl;
        currentIndex = targetIndex;
        viewer.dataset.loading = 'false';
        setStatus(formatIndex(currentIndex));
        warmNearbyFrames(currentIndex, direction);
      } catch {
        if (generation !== loadGeneration) return;
        viewer.dataset.loading = 'false';
        setStatus('画像を読み込めませんでした。通信状態をご確認のうえ、もう一度お試しください。');
        if (retryButton) retryButton.hidden = false;
      }
    }

    function loadManifest() {
      if (manifestPromise) return manifestPromise;
      setStatus('画像を準備しています…');
      manifestPromise = fetch(manifestUrl, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('manifest-load-failed');
          return response.json();
        })
        .then((manifest) => {
          const entries = Array.isArray(manifest.frames) ? manifest.frames : [];
          if (manifest.productHandle !== 'ezgo2' || entries.length < 2 || entries.length > 120) {
            throw new Error('manifest-invalid');
          }
          const ordered = entries
            .map((frame) => ({ index: Number(frame.index), url: String(frame.url || '') }))
            .sort((left, right) => left.index - right.index);
          if (ordered.some((frame, index) => frame.index !== index + 1 || !frame.url.startsWith('https://cdn.shopify.com/'))) {
            throw new Error('manifest-invalid');
          }

          frames = ordered;
          viewer.dataset.ready = 'true';
          stage.tabIndex = 0;
          stage.setAttribute('aria-label', '左右の矢印キー、またはドラッグでEzGo2の向きを切り替えられます');
          controls.forEach((control) => { control.hidden = false; });
          if (hint) hint.hidden = false;
          if (retryButton) retryButton.hidden = true;
          setStatus(formatIndex(currentIndex));
          return manifest;
        })
        .catch(() => {
          manifestPromise = null;
          setStatus('画像を表示できませんでした。時間をおいてもう一度お試しください。');
          if (retryButton) retryButton.hidden = false;
          throw new Error('manifest-load-failed');
        });
      return manifestPromise;
    }

    function moveBy(delta) {
      const baseIndex = targetIndex;
      void loadManifest()
        .then(() => showFrame(baseIndex + delta, delta < 0 ? -1 : 1))
        .catch(() => {});
    }

    controls.forEach((control) => {
      control.addEventListener('click', () => {
        const delta = Number(control.dataset.step);
        if (Number.isFinite(delta) && delta !== 0) moveBy(delta);
      });
    });

    stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveBy(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveBy(-1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        void loadManifest().then(() => showFrame(0, -direction)).catch(() => {});
      }
    });

    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        baseIndex: targetIndex,
        stepPixels: Math.max(6, stage.getBoundingClientRect().width / 40),
        lastSteps: 0,
        isHorizontal: false,
      };
    });

    stage.addEventListener('pointermove', (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.isHorizontal && Math.abs(dx) > 7 && Math.abs(dx) > Math.abs(dy) * 1.15) {
        drag.isHorizontal = true;
        stage.dataset.dragging = 'true';
        if (stage.setPointerCapture) stage.setPointerCapture(event.pointerId);
      }
      if (!drag.isHorizontal) return;

      event.preventDefault();
      const steps = Math.trunc(dx / drag.stepPixels);
      if (steps === drag.lastSteps) return;
      drag.lastSteps = steps;
      const baseIndex = drag.baseIndex;
      void loadManifest()
        .then(() => showFrame(baseIndex + steps, steps < 0 ? -1 : steps > 0 ? 1 : direction))
        .catch(() => {});
    });

    function finishDrag(event) {
      if (!drag || drag.pointerId !== event.pointerId) return;
      if (stage.hasPointerCapture && stage.hasPointerCapture(event.pointerId)) {
        stage.releasePointerCapture(event.pointerId);
      }
      stage.dataset.dragging = 'false';
      drag = null;
    }

    stage.addEventListener('pointerup', finishDrag);
    stage.addEventListener('pointercancel', finishDrag);
    stage.addEventListener('lostpointercapture', () => {
      stage.dataset.dragging = 'false';
      drag = null;
    });

    if (retryButton) {
      retryButton.addEventListener('click', () => {
        retryButton.hidden = true;
        if (frames.length) void showFrame(targetIndex, direction);
        else void loadManifest().catch(() => {});
      });
    }

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          void loadManifest().catch(() => {});
        }
      }, { rootMargin: '160px 0px' })
      : null;

    if (observer) observer.observe(viewer);
    else void loadManifest().catch(() => {});
  }

  function initializeAll() {
    document.querySelectorAll(selector).forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }
})();
